import { upperFirst } from 'lodash';
import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { preventEnter } from 'core/utils';
import { PartBomType } from 'partBoms/enums';
import { useGetUoms } from 'parts/hooks';
import { IUom } from 'parts/types';

import { CreateTypeFormField } from './fields';

interface IProps {
  onClose: () => void;
  isEdit?: boolean;
}

export const DetailsFormFields = forwardRef(function DetailsFormFields(
  { onClose, isEdit = false }: IProps,
  ref
) {
  const { t } = useTranslation();

  const { getValues, trigger } = useFormContext();

  const { records: uoms } = useGetUoms({ filters: { bomDisplay: 1 } });

  useImperativeHandle(
    ref,
    () => ({
      triggerFormSubmit: () => {
        return new Promise((resolve, reject) => {
          trigger()
            .then((isValid) => {
              if (isValid) {
                resolve(getValues());
              } else {
                reject();
              }
            })
            .catch(reject);
        });
      },
    }),
    [getValues, trigger]
  );

  return (
    <Form onClose={onClose} isOpen inline withoutButtons withoutNotification>
      {!isEdit && <CreateTypeFormField />}

      <FormField
        name="bomType"
        type={InputType.SELECT}
        inputProps={{
          placeholder: t('placeholderPartBomType'),
          label: t('labelPartBomType'),
          options: Object.values(PartBomType).map((type) => ({
            label: upperFirst(type),
            value: type,
          })),
          required: true,
        }}
      />

      <FormField
        name="description"
        type={InputType.TEXTAREA}
        inputProps={{
          placeholder: t('placeholderPartBomDescription'),
          label: t('labelPartBomDescription'),
          onKeyDown: preventEnter,
          maxRows: 6,
          required: true,
        }}
      />

      <FormField
        name="uom"
        type={InputType.SELECT}
        inputProps={{
          label: t('labelPartBomUom'),
          placeholder: t('placeholderPartBomUom'),
          getLabel: (option: IUom) => option?.name,
          getValue: (option: IUom) => option?.name,
          options: uoms,
          required: true,
        }}
      />
    </Form>
  );
});
